import {useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router-dom";
import {useForm, Controller} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {TextInput} from "../../Components/Form/TextInput";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {PasswordInput} from "../../Components/Form/PasswordInput";
import check from "../../Assets/images/check.png";
import {useEffect, useState} from "react";
import {SelectInput} from "../../Components/Form/SelectInput";
import {LanguageSelector} from "../../Components/Form/LanguageSelector";
import {FileUpload} from "../../Components/Form/FileUpload";
import {TextAreaInput} from "../../Components/Form/TextAreaInput";
import Logo from '../../Assets/images/logo/connectcaremintgreen.png';
import * as React from "react";
import {useBoundStore} from "../../Store/store";
import {FeeCalculator} from "../../Utils/FeeCalculator";
import {TosFreelancerUnstyled} from "../../Components/Legal/TosFreelancer";
import {ControlledSwitch} from "../../Components/Form/ControlledSwitch";
import {prepareConsentData} from "../../Utils/ConsentTracker";
import {validationErrorKey} from "../../Utils/FormHelpers";
import {Switch} from "@headlessui/react";

export const RegisterFreelancer = builder => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const registerFreelancer = useBoundStore(state => state.registerFreelancer);
    const checkEmailAvailable = useBoundStore(state => state.checkEmailAvailable);

    const jobFunctions = [
        {value: 'A1', label: t('functions.A1'), id: 0},
        {value: 'A2', label: t('functions.A2'), id: 1},
        {value: 'A0', label: t('functions.A0'), id: 2},
        {value: 'A3', label: t('functions.A3'), id: 3}
    ]

    const mainStatutenOptions = [
        {value: 'freelance', label: t('statuten.freelance'), id: 0},
        {value: 'interim', label: t('statuten.interim'), id: 1},
    ];

    const extraStatutenByMain = {
        freelance: [
            {value: 'interim', label: t('statuten.interim'), id: 1},
        ],
        interim: [
            {value: 'student', label: t('statuten.student'), id: 2},
            {value: 'flexi_job', label: t('statuten.flexi_job'), id: 3},
        ],
    };

    const [currentStep, setCurrentStep] = useState(1);
    const [counterOffer, setCounterOffer] = useState(false);
    const [currentHourRate, setCurrentHourRate] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const schema = yup
        .object({
            firstname: yup.string().required(t('general.errors.first_name_empty')),
            lastname: yup.string().required(t('general.errors.last_name_empty')),
            email: yup.string().email(t('general.errors.email_invalid')).required(t('general.errors.email_empty')),
            tel: yup.string().required(t('general.errors.phone_empty')),
            function: yup.string().required(t('general.errors.function_empty')),
            main_statuut: yup.string().oneOf(['freelance', 'interim']).required(),
            extra_statuten: yup.array().of(yup.string()).nullable(),
            experience: yup.number()
                .transform((value, originalValue) => {
                    if (originalValue === '' || originalValue === null || originalValue === undefined) {
                        return undefined;
                    }
                    return value;
                })
                .typeError(t('yup_errors.experience_valid_number'))
                .integer(t('yup_errors.experience_integer'))
                .min(0, t('yup_errors.experience_min_0'))
                .max(70, t('yup_errors.experience_max_70'))
                .required(t('general.errors.experience_empty')),
            street: yup.string().required(t('general.errors.street_empty')),
            house_number: yup.string().required(t('general.errors.number_empty')),
            bus: yup.string(),
            city: yup.string().required(t('general.errors.city_empty')),
            zip: yup.string().required(t('general.errors.zip_empty')),
            country: yup.string().required(t('general.errors.country_empty')),
            company_name: yup.string().when('main_statuut', {
                is: 'freelance',
                then: (schema) => schema.required(t('general.errors.company_name_empty')),
                otherwise: (schema) => schema.nullable(),
            }),
            vat_number: yup.string().when('main_statuut', {
                is: 'freelance',
                then: (schema) => schema.required(t('general.errors.vat_empty')),
                otherwise: (schema) => schema.nullable(),
            }),
            bank_account_number: yup.string().required(t('freelancers_module.bank_account_required')),
            province: yup.string().required(t('general.errors.province_empty')).oneOf(['antwerpen', 'limburg', 'oost-vlaanderen', 'vlaams-brabant', 'west-vlaanderen','henegouwen','luik','luxemburg','namen','waals-brabant','brussels-hoofdstedelijk-gewest']),
            diploma: yup.mixed()
                .test("required", t('file_upload.required'), (file) => {
                    if (file === null || file === undefined) return false
                    if (file.length > 0) return true
                    return false
                })
                .test("fileSize", t('file_upload.too_large_25mb'), (file) => {
                    if (!file || file.length === 0) return true
                    const maxSize = 25 * 1024 * 1024;
                    return file[0]?.size <= maxSize
                }),
            visa: yup.mixed()
                .test("required", t('file_upload.required'), (file) => {
                    if (file === null || file === undefined) return false
                    if (file.length > 0) return true
                    return false
                })
                .test("fileSize", t('file_upload.too_large_25mb'), (file) => {
                    if (!file || file.length === 0) return true
                    const maxSize = 25 * 1024 * 1024;
                    return file[0]?.size <= maxSize
                }),
            dont_agree_with_rate: yup.boolean(),
            dont_agree_with_rate_explanation: yup.string().when("dont_agree_with_rate", {
                is: (value) => {return value},
                then: () => yup
                    .string()
                    .min(10, t('general.errors.min_10_chars'))
                    .required(t('general.errors.rate_explenation_empty'))
            }),
            agree_with_terms: yup.boolean().oneOf([true], t('general.errors.tos_not_accepted')),
            agree_with_privacy: yup.boolean().oneOf([true], t('yup_errors.privacy_not_accepted')),
            password: yup.string().min(8, t('login.password_requirement'))
                .required(t('general.errors.password_empty'))
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
                    t('login.password_requirement')
                ),
            password_confirmation: yup.string().oneOf([yup.ref('password'), null], t('general.errors.password_no_match')).required(t('general.errors.password_empty')),
        })
        .required()

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        control,
        watch,
        setValue,
        getValues
    } = useForm({
        resolver: yupResolver(schema),
        reValidateMode: 'onBlur',
        mode: 'onChange',
        defaultValues: {
            main_statuut: 'freelance',
            extra_statuten: [],
            locale: 'nl',
        }
    })

    const [selectedFunction, selectedYearsOfExperience, selectedMainStatuut] = watch(['function', 'experience', 'main_statuut']);

    useEffect(() => {
        const allowed = extraStatutenByMain[selectedMainStatuut] ?? [];
        const allowedValues = allowed.map(o => o.value);
        const current = getValues('extra_statuten') || [];
        const filtered = current.filter(v => allowedValues.includes(v));
        if (filtered.length !== current.length) {
            setValue('extra_statuten', filtered);
        }
    }, [selectedMainStatuut]);

    const progressSteps = async () => {
        switch (currentStep) {
            case 1: {
                const baseFields = ["firstname", "lastname", "email", "function", "experience", "main_statuut", "street", "house_number", "tel", "city", "zip", "country",
                    "password", "password_confirmation", "province", "bank_account_number"];
                const step1Fields = getValues('main_statuut') === 'freelance'
                    ? [...baseFields, "company_name", "vat_number"]
                    : baseFields;
                const valid = await trigger(step1Fields, {shouldFocus: true});
                if (!valid) return;

                const email = getValues('email');
                const available = await checkEmailAvailable(email);
                if (!available) {
                    toast.error(t('register_page.email_in_use'));
                    return;
                }

                setCurrentStep(2);
                break;
            }
            case 2: {
                const step2Fields = getValues('main_statuut') === 'freelance'
                    ? ["diploma", "visa", "dont_agree_with_rate_explanation"]
                    : ["diploma", "visa"];
                trigger(step2Fields, {shouldFocus: true}).then(bool => bool ? setCurrentStep(3) : false)
                break;
            }
        }
    }

    const onSubmit = async (data) => {
        if (isSubmitting) return;

        const consentValid = await trigger(["agree_with_terms", "agree_with_privacy"], {shouldFocus: true});
        if (!consentValid) return;

        setIsSubmitting(true);
        try {
            await registerFreelancer({
                ...data,
                ...prepareConsentData(data.agree_with_terms, data.agree_with_privacy)
            });
            toast.success(t('register_page.success'), {
                duration: 6000,
                position: 'top-center',
                style: {zIndex: 9999},
            });
            setTimeout(() => setCurrentStep(currentStep + 1), 1000);
        } catch (error) {
            let message = t('register_page.generic_error');
            if (error?.response?.status === 422) {
                const errKey = validationErrorKey(error.response.data?.errors);
                if (errKey) {
                    message = t(errKey.key, {defaultValue: t(errKey.fallbackKey)});
                }
            }
            toast.error(message);
            console.error('Error creating freelancer', error);
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        if (selectedFunction && selectedYearsOfExperience) {
            setCurrentHourRate(FeeCalculator(selectedYearsOfExperience, selectedFunction))
        }
    }, [selectedFunction, selectedYearsOfExperience]);

    const dontAgreeWithRate = watch('dont_agree_with_rate');

    return (
        <div className={'flex flex-col justify-center min-h-screen w-full h-full'}>
            <div className={'p-3 text-center'}>
                <img src={Logo} alt={'Logo for connect care mint green'} className={'max-h-16 mx-auto'}/>
            </div>
            <div className={'p-3 w-full max-w-[80%] md:max-w-[70%] mx-auto'}>

                <h1 className={'text-2xl font-bold text-center'}>{t('register_page.title')}</h1>
                <p className={'text-center text-sm text-gray-500 mb-4'}>{t('register_page.subtitle')}</p>

                <p className={'text-sm text-center text-gray-500'}>{t('register_page.have_account')}
                    <Link
                        to={'/login'}
                        className={'text-primary hover:underline ml-1 text-sm text-center'}
                    >
                        {t('register_page.back_to_login')}
                    </Link>
                </p>
            </div>
            <BaseCard>
                <ul className="relative flex flex-row gap-x-2  justify-center">
                    <li className="flex items-center gap-x-2 -gap-r-1 shrink basis-0 flex-1 group">
                        <div
                            className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                        <span
                            className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep > 1 ? 'finishedStep' : ''} ${currentStep === 1 ? 'activeStep' : ''}`}>1</span>
                            <span className="ms-2 block text-sm font-medium text-gray-800">{t('register_page.step_info')}</span>
                        </div>
                        <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                    </li>

                    <li className="flex items-center gap-x-2 shrink basis-0 flex-1 group">
                        <div
                            className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                        <span
                            className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep > 2 ? 'finishedStep' : ''} ${currentStep === 2 ? 'activeStep' : ''}`}>2</span>
                            <span className="ms-2 block text-sm font-medium text-gray-800">{t('register_page.step_documents')}</span>
                        </div>
                        <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                    </li>

                    <li className="flex items-center gap-x-2 shrink basis-0 flex-1 group">
                        <div
                            className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                        <span
                            className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep > 3 ? 'finishedStep' : ''} ${currentStep === 3 ? 'activeStep' : ''}`}>3</span>
                            <span className="ms-2 block text-sm font-medium text-gray-800">{t('register_page.step_tos')}</span>
                        </div>
                        <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                    </li>

                    <li className="flex items-center gap-x-2 shrink basis-0 flex-1 group">
                        <div
                            className="min-w-7 min-h-7 inline-flex justify-center items-center text-xs align-middle">
                        <span
                            className={`size-7 flex justify-center items-center flex-shrink-0 bg-white border border-gray-200 font-medium text-gray-800 rounded-full ${currentStep === 4 ? 'activeStep' : ''}`}>4</span>
                            <span className="ms-2 block text-sm font-medium text-gray-800">{t('register_page.step_confirm')}</span>
                        </div>
                        <div className="w-full h-px flex-1 bg-gray-200 group-last:hidden"></div>
                    </li>
                </ul>

                <hr className="mt-4 mb-1 border-gray-200"/>

                <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
                {
                    currentStep === 1 && (
                        <>
                            {/* CCPSOM-30: language selector disabled until multi-language support is ready
                            <div className="mb-4 max-w-xs">
                                <LanguageSelector
                                    label={t('language.label')}
                                    value={getValues('locale')}
                                    onChange={(loc) => setValue('locale', loc)}
                                />
                            </div> */}

                            <h2 className={'text-md text-gray-700 uppercase mb-1 mt-2'}>{t('register_page.section_personal_info')}</h2>
                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                <TextInput
                                    name={'firstname'}
                                    register={register}
                                    translationIndex={'general.fields.firstname'}
                                    errors={errors}
                                />
                                <TextInput
                                    name={'lastname'}
                                    register={register}
                                    translationIndex={'general.fields.lastname'}
                                    errors={errors}
                                />
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                <TextInput
                                    name={'email'}
                                    register={register}
                                    translationIndex={'general.fields.email'}
                                    errors={errors}
                                />

                                <TextInput
                                    name={'tel'}
                                    register={register}
                                    translationIndex={'general.fields.tel'}
                                    errors={errors}
                                />
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                <SelectInput
                                    name={'function'}
                                    register={register}
                                    control={control}
                                    setValue={setValue}
                                    translationIndex={'general.fields.function'}
                                    options={jobFunctions}
                                    errors={errors}
                                />

                                <TextInput
                                    name={'experience'}
                                    type={'number'}
                                    register={register}
                                    translationIndex={'general.fields.experience'}
                                    errors={errors}
                                />
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                <SelectInput
                                    name={'main_statuut'}
                                    control={control}
                                    setValue={setValue}
                                    options={mainStatutenOptions}
                                    currentValue={getValues('main_statuut') ?? 'freelance'}
                                    translationIndex={'general.fields.main_statuut'}
                                    errors={errors}
                                />
                                <SelectInput
                                    name={'extra_statuten'}
                                    control={control}
                                    setValue={setValue}
                                    multiple={true}
                                    options={extraStatutenByMain[selectedMainStatuut] ?? []}
                                    currentValue={getValues('extra_statuten') ?? []}
                                    translationIndex={'general.fields.extra_statuten'}
                                    helperText={t('general.fields.extra_statuten_helper')}
                                    errors={errors}
                                />
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                <PasswordInput
                                    name={'password'}
                                    register={register}
                                    translationIndex={'general.fields.password'}
                                    errors={errors}
                                />

                                <PasswordInput
                                    name={'password_confirmation'}
                                    register={register}
                                    translationIndex={'general.fields.confirm_password'}
                                    errors={errors}
                                />
                            </div>

                            <h2 className={'text-md text-gray-700 uppercase mt-4 mb-1'}>{t('register_page.section_company_address')}</h2>
                            <div className={'mb-1 mt-1'}>
                                <TextInput
                                    name={'street'}
                                    register={register}
                                    translationIndex={'general.fields.street'}
                                    errors={errors}
                                />
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 gap-4'}>
                                <TextInput
                                    name={'house_number'}
                                    register={register}
                                    translationIndex={'general.fields.nr'}
                                    errors={errors}
                                />

                                <TextInput
                                    name={'bus'}
                                    register={register}
                                    translationIndex={'general.fields.bus'}
                                    errors={errors}
                                />

                                <SelectInput
                                    name={'province'}
                                    options={[
                                        {id: 1, value: 'antwerpen', 'label': t('provinces.antwerp')},
                                        {id: 2, value: 'limburg', 'label': t('provinces.limburg')},
                                        {id: 3, value: 'oost-vlaanderen', 'label': t('provinces.east_flanders')},
                                        {id: 4, value: 'vlaams-brabant', 'label': t('provinces.flemish_brabant')},
                                        {id: 5, value: 'west-vlaanderen', 'label': t('provinces.west_flanders')},
                                        {id: 6, value: 'henegouwen', 'label': t('provinces.hainaut')},
                                        {id: 7, value: 'luik', 'label': t('provinces.liege')},
                                        {id: 8, value: 'luxemburg', 'label': t('provinces.luxembourg')},
                                        {id: 9, value: 'namen', 'label': t('provinces.namur')},
                                        {id: 10, value: 'waals-brabant', 'label': t('provinces.walloon_brabant')},
                                        {id: 11, value: 'brussels-hoofdstedelijk-gewest', 'label': t('provinces.brussels')}
                                    ]}
                                    setValue={setValue}
                                    control={control}
                                    value={getValues('province') ?? null}
                                    placeholder={t('general.fields.province')}
                                    translationIndex={'general.fields.province'}
                                    errors={errors}
                                />
                            </div>

                            <div className={'grid grid-cols-1 md:grid-cols-3 mb-1 gap-4'}>
                                <TextInput
                                    name={'city'}
                                    register={register}
                                    translationIndex={'general.fields.city'}
                                    errors={errors}
                                />

                                <TextInput
                                    name={'zip'}
                                    register={register}
                                    translationIndex={'general.fields.postal_code'}
                                    errors={errors}
                                />

                                <TextInput
                                    name={'country'}
                                    register={register}
                                    translationIndex={'general.fields.country'}
                                    errors={errors}
                                />
                            </div>

                            {selectedMainStatuut === 'freelance' && (
                                <>
                                    <h2 className={'text-md text-gray-700 uppercase mb-1 mt-4'}>{t('register_page.section_company_info')}</h2>
                                    <div className={'mt-1 mb-1'}>
                                        <TextInput
                                            name={'company_name'}
                                            register={register}
                                            translationIndex={'general.fields.company_name'}
                                            errors={errors}
                                        />
                                    </div>

                                    <div className={'mt-1 mb-1'}>
                                        <TextInput
                                            name={'vat_number'}
                                            register={register}
                                            translationIndex={'general.fields.vat'}
                                            errors={errors}
                                        />
                                    </div>
                                </>
                            )}

                            <div className={'mt-1 mb-1'}>
                                <TextInput
                                    name={'bank_account_number'}
                                    register={register}
                                    translationIndex={'general.fields.bank_account'}
                                    errors={errors}
                                />
                            </div>
                        </>
                    )
                }

                {
                    currentStep === 2 && (
                        <div>
                            <h2 className={'text-xl font-bold mb-1'}>{t('register_page.required_documents')}</h2>

                            <div className={'grid grid-cols-1 md:grid-cols-2 mb-1 md:gap-4'}>
                                <FileUpload
                                    label={'Diploma'}
                                    name={'diploma'}
                                    register={register}
                                    translationIndex={'general.fields.diploma'}
                                    errors={errors}
                                    control={control}
                                />

                                <FileUpload
                                    label={'Visa'}
                                    name={'visa'}
                                    register={register}
                                    translationIndex={'general.fields.visa'}
                                    errors={errors}
                                    control={control}
                                />
                            </div>
                            {selectedMainStatuut === 'freelance' && (
                                <>
                                    <table className={'w-full my-4'}>
                                        <thead>
                                        <tr>
                                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 bg-opacity-40 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                <div>
                                                    {t('general.fields.experience')}
                                                </div>
                                            </th>
                                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 bg-opacity-40 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                <div>
                                                    {t('general.fields.function')}
                                                </div>
                                            </th>
                                            <th className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 bg-opacity-40 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                <div>
                                                    {t('general.fields.hourly_rate')}
                                                </div>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr>
                                            <td className={'px-5 py-3'}>{selectedYearsOfExperience}</td>
                                            <td className={'px-5 py-3'}>{jobFunctions.map((item) => {
                                                if (item.value === selectedFunction) return item.label
                                            })}</td>
                                            <td className={'px-5 py-3'}>{t('units.per_hour', {amount: `€ ${FeeCalculator(selectedYearsOfExperience, selectedFunction)}`})}</td>
                                        </tr>
                                        </tbody>
                                    </table>
                                    <div className={'mb-2 w-full mt-4 cursor-pointer flex items-start'}>
                                        <ControlledSwitch
                                            control={control}
                                            name={'dont_agree_with_rate'}
                                            getValues={getValues}
                                            setValue={setValue}
                                            errors={errors}
                                            translationIndex={'general.fields.dont_agree_with_rate'}
                                        />
                                    </div>

                                    {
                                        dontAgreeWithRate && (
                                            <TextAreaInput
                                                register={register}
                                                translationIndex={'general.fields.counter_offer'}
                                                name={'dont_agree_with_rate_explanation'}
                                                errors={errors}
                                            />
                                        )
                                    }
                                </>
                            )}

                            {selectedMainStatuut !== 'freelance' && (
                                <div className="my-4 p-3 rounded bg-purple-50 border border-purple-200 text-sm text-purple-900">
                                    {t('register_page.interim_pratoflex_info')}
                                </div>
                            )}

                            {selectedMainStatuut === 'freelance' && (
                                <p className={'text-sm text-gray-600 mt-4 opacity-60 text-center whitespace-pre-line'}>{t('register_page.subscription_notice')}</p>
                            )}
                        </div>
                    )
                }

                    {
                        currentStep === 3 && (
                            <div>
                                <h2 className={'text-xl font-bold mb-1'}>TOS</h2>

                                <div
                                    className={'max-h-64 overflow-auto mb-4 w-full p-3 my-1 bg-gray-100 text-gray-500 rounded-md '}
                                    disabled={true}>
                                <TosFreelancerUnstyled />
                            </div>

                            <div className={'my-4'}>
                                <ControlledSwitch
                                    control={control}
                                    name={'agree_with_terms'}
                                    getValues={getValues}
                                    setValue={setValue}
                                    errors={errors}
                                    translationIndex={'general.fields.tos_accept'}
                                />
                            </div>

                            <div className={'my-4'}>
                                <div className="flex flex-row">
                                    <Controller
                                        control={control}
                                        defaultValue={false}
                                        name={'agree_with_privacy'}
                                        rules={{required: true}}
                                        render={({field: {ref, ...rest}}) => (
                                            <Switch
                                                {...rest}
                                                checked={getValues('agree_with_privacy')}
                                                onChange={() => {
                                                    setValue('agree_with_privacy', !getValues('agree_with_privacy'))
                                                }}
                                                value={getValues('agree_with_privacy')}
                                                className={`${
                                                    getValues('agree_with_privacy') ? 'bg-primary' : 'bg-gray-200'
                                                } relative inline-flex h-6 w-11 items-center rounded-full`}
                                            >
                                                <span className="sr-only">{t('register_page.privacy_switch_sr')}</span>
                                                <span
                                                    className={`${getValues('agree_with_privacy') ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                                                />
                                            </Switch>
                                        )}
                                    />
                                    <p className="pl-2">
                                        {t('register_page.privacy_accept_pre')}{' '}
                                        <a
                                            href="/manuals/privacy_policy-v1.pdf"
                                            download
                                            className="text-primary hover:underline font-medium"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            {t('register_page.privacy_link')}
                                        </a>
                                        {' '}{t('register_page.privacy_accept_post')}
                                    </p>
                                </div>
                                {errors.agree_with_privacy && (
                                    <p className="text-red-500">{errors.agree_with_privacy.message}</p>
                                )}
                            </div>
                        </div>
                    )
                }

                {
                    currentStep === 4 && (
                        <div>
                            <div className={'flex flex-col justify-center w-full'}>
                                <img src={check} alt={'Logo for connect care mint green'}
                                     className={'max-h-16 mx-auto'}/>

                                <p className={'mt-4 text-center'}>
                                    {t('register.registration_received')}<br/>
                                    {t('register.result_sent_to_email')}
                                </p>
                            </div>
                        </div>
                    )
                }

                <div className={'mt-4 mb-1'}>
                    {
                        currentStep === 3 && (
                            <PrimaryButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? t('register_page.submitting') : t('login.cta_register')}
                            </PrimaryButton>
                        )
                    }

                    {
                        currentStep === 4 && (
                            <PrimaryButton onClick={() => navigate('/')}>
                                {t('login.cta_login')}
                            </PrimaryButton>
                        )
                    }

                    {
                        currentStep < 3 && (
                            <PrimaryButton onClick={progressSteps}>
                                {t('general.actions.next')}
                            </PrimaryButton>
                        )
                    }

                    {
                        currentStep > 1 && currentStep !== 4 && (
                            <PrimaryButton outlined={true} onClick={() => setCurrentStep(currentStep - 1)}
                                           className={'mt-2'}>
                                {t('general.actions.previous')}
                            </PrimaryButton>
                        )
                    }
                </div>
            </form>
            </BaseCard>
        </div>
    )
}